export interface SupplySection {
    header: string;
    content: { label: string; value: string; nested?: SupplySection[] }[];
    expanded?: boolean;
    isNested?: boolean;
}

export interface SupplyAttributesData {
    rootFields: { label: string; value: string }[];
    sections: SupplySection[];
}

function formatDate(dateValue: any): string {
    if (!dateValue) return 'N/A';
    
    const date = new Date(dateValue);
    
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

function formatTimestamp(timestamp: number | undefined): string {
    if (timestamp === undefined || timestamp === null) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleString();
}

function buildLocationSection(location: any): SupplySection | null {
    if (!location) return null;

    const coord = location.coordinate;
    const coordinateNested: SupplySection = {
        header: 'Coordinate',
        content: [
            { label: 'Latitude', value: (coord && coord.lat) ? coord.lat.toFixed(6) : 'N/A' },
            { label: 'Longitude', value: (coord && coord.lng) ? coord.lng.toFixed(6) : 'N/A' }
        ],
        expanded: false,
        isNested: true
    };

    return {
        header: 'Location',
        content: [
            { label: 'Enabled', value: location.enabled ? 'Yes' : 'No' },
            { 
                label: 'Coordinate', 
                value: 'View Details',
                nested: [coordinateNested]
            },
            { label: 'City', value: location.cityName || 'N/A' },
            { label: 'City ID', value: location.cityId || 'N/A' },
            { label: 'Hex7', value: location.hex7 || 'N/A' },
            { label: 'Hex8', value: location.hex8 || 'N/A' },
            { label: 'Hex9', value: location.hex9 || 'N/A' },
            { label: 'Hex10', value: location.hex10 || 'N/A' },
            { label: 'Updated', value: formatDate(location.updatedAt) || 'N/A' }
        ],
        expanded: false,
        isNested: false
    };
}

function buildServicesSection(services: any): SupplySection | null {
    if (!services || !services.serviceDetails || services.serviceDetails.length === 0) {
        return null;
    }

    const serviceDetailsSections: SupplySection[] = services.serviceDetails.map((service: any, index: number) => ({
        header: `${index + 1}: ${service.name || service.id}`,
        content: [
            { label: 'ID', value: service.id || 'N/A' },
            { label: 'Name', value: service.name || 'N/A' },
            { 
                label: 'Duration', 
                value: service.duration 
                    ? `${new Date(service.duration.start).toLocaleString()} - ${new Date(service.duration.end).toLocaleString()}` 
                    : 'N/A' 
            }
        ],
        expanded: false,
        isNested: true
    }));

    return {
        header: 'Services',
        content: [
            { label: 'Total Services', value: services.serviceDetails.length.toString() },
            { 
                label: 'Service Details', 
                value: 'View All',
                nested: serviceDetailsSections
            },
            { label: 'Updated', value: formatTimestamp(services.updatedAt) || 'N/A' }
        ],
        expanded: false,
        isNested: false
    };
}

function buildStatusSection(status: any): SupplySection | null {
    if (!status) return null;

    const content: { label: string; value: string; nested?: SupplySection[] }[] = [
        { label: 'Value', value: status.value || 'N/A' },
        { label: 'Updated', value: formatDate(status.updatedAt) || 'N/A' }
    ];

    if (status.softStatus) {
        const softStatusNested: SupplySection = {
            header: 'Soft Status',
            content: [
                { label: 'Value', value: status.softStatus.value || 'N/A' },
                { label: 'Context', value: status.softStatus.context || 'N/A' },
                { label: 'Source', value: status.softStatus.source || 'N/A' }
            ],
            expanded: false,
            isNested: true
        };
        content.push({
            label: 'Soft Status',
            value: 'View Details',
            nested: [softStatusNested]
        });
    }

    return {
        header: 'Status',
        content: content,
        expanded: false,
        isNested: false
    };
}

function buildWayPointsSection(wayPoints: any[]): SupplySection | null {
    if (!wayPoints || wayPoints.length === 0) return null;

    const wayPointSections: SupplySection[] = wayPoints.map((wp, index) => {
        const coordinateNested: SupplySection = {
            header: 'Coordinate',
            content: [
                { label: 'Latitude', value: (wp.coordinate && wp.coordinate.lat) ? wp.coordinate.lat.toFixed(6) : 'N/A' },
                { label: 'Longitude', value: (wp.coordinate && wp.coordinate.lng) ? wp.coordinate.lng.toFixed(6) : 'N/A' }
            ],
            expanded: false,
            isNested: true
        };

        return {
            header: `${wp.type === 'pickup' ? '📍 Pickup' : '🏁 Drop'} ${index + 1}`,
            content: [
                { label: 'Order ID', value: wp.orderId || 'N/A' },
                { label: 'Service', value: wp.serviceDetailName || wp.serviceDetailId || 'N/A' },
                { 
                    label: 'Coordinate', 
                    value: 'View Location',
                    nested: [coordinateNested]
                },
                { label: 'Hex7', value: wp.hex7 || 'N/A' },
                { label: 'Hex8', value: wp.hex8 || 'N/A' },
                { label: 'Updated', value: formatDate(wp.updatedAt) || 'N/A' }
            ],
            expanded: false,
            isNested: true
        };
    });

    return {
        header: `Way Points (${wayPoints.length})`,
        content: [
            { 
                label: 'All Way Points', 
                value: `${wayPoints.length} points`,
                nested: wayPointSections
            }
        ],
        expanded: false,
        isNested: false
    };
}

function buildOnRideBookingSection(onRideBooking: any): SupplySection | null {
    if (!onRideBooking) return null;

    const content: { label: string; value: string; nested?: SupplySection[] }[] = [
        { label: 'Enabled', value: onRideBooking.enabled ? 'Yes' : 'No' },
        { label: 'Accept Type', value: onRideBooking.acceptType || 'N/A' },
        { label: 'Updated', value: formatDate(onRideBooking.updatedAt) || 'N/A' }
    ];

    if (onRideBooking.currentOrder) {
        const order = onRideBooking.currentOrder;
        const dropLocationNested: SupplySection = {
            header: 'Drop Location',
            content: [
                { 
                    label: 'Latitude', 
                    value: (order.dropLocation && order.dropLocation.coordinate && order.dropLocation.coordinate.lat) 
                        ? order.dropLocation.coordinate.lat.toFixed(6) 
                        : 'N/A' 
                },
                { 
                    label: 'Longitude', 
                    value: (order.dropLocation && order.dropLocation.coordinate && order.dropLocation.coordinate.lng) 
                        ? order.dropLocation.coordinate.lng.toFixed(6) 
                        : 'N/A' 
                },
                { label: 'City', value: (order.dropLocation && order.dropLocation.cityName) ? order.dropLocation.cityName : 'N/A' }
            ],
            expanded: false,
            isNested: true
        };

        const currentOrderNested: SupplySection = {
            header: 'Current Order',
            content: [
                { label: 'Order ID', value: order.orderId || 'N/A' },
                { label: 'Service', value: order.serviceDetailName || order.serviceDetailId || 'N/A' },
                {
                    label: 'Drop Location',
                    value: 'View Location',
                    nested: [dropLocationNested]
                }
            ],
            expanded: false,
            isNested: true
        };

        content.push({
            label: 'Current Order',
            value: 'View Details',
            nested: [currentOrderNested]
        });
    } else {
        content.push({ label: 'Current Order', value: 'None' });
    }

    return {
        header: 'On Ride Booking',
        content: content,
        expanded: false,
        isNested: false
    };
}

function buildRoutePreferenceSection(routePreference: any): SupplySection | null {
    if (!routePreference) return null;

    const content: { label: string; value: string; nested?: SupplySection[] }[] = [
        { label: 'Exclusive Preferred Routes', value: routePreference.exclusivePreferredRouteOrders ? 'Yes' : 'No' },
        { label: 'Updated', value: formatDate(routePreference.updatedAt) || 'N/A' }
    ];

    if (routePreference.routes && routePreference.routes.length > 0) {
        const routeSections: SupplySection[] = routePreference.routes.map((route: any, index: number) => ({
            header: `Route ${index + 1}`,
            content: [
                { label: 'Route ID', value: route.routeID || route._id || 'N/A' },
                { label: 'Enabled', value: route.enabled ? 'Yes' : 'No' },
                { label: 'Enabled At', value: formatDate(route.enabledAt) || 'N/A' },
                { label: 'Enabled Till', value: formatDate(route.enabledTill) || 'N/A' },
                { label: 'Address ID', value: route.addressId || 'N/A' }
            ],
            expanded: false,
            isNested: true
        }));

        content.push({
            label: 'Routes',
            value: `${routePreference.routes.length} routes`,
            nested: routeSections
        });
    } else {
        content.push({ label: 'Routes', value: 'None' });
    }

    return {
        header: 'Route Preference',
        content: content,
        expanded: false,
        isNested: false
    };
}

function buildMicroCorridorSection(microCorridor: any): SupplySection | null {
    if (!microCorridor) return null;

    const content: { label: string; value: string; nested?: SupplySection[] }[] = [
        { label: 'Enabled', value: microCorridor.enabled ? 'Yes' : 'No' },
        { label: 'Updated', value: formatDate(microCorridor.updatedAt) || 'N/A' }
    ];

    if (microCorridor.corridors && microCorridor.corridors.length > 0) {
        const corridorSections: SupplySection[] = microCorridor.corridors.map((corridor: any, index: number) => ({
            header: `Corridor ${index + 1}: ${corridor.name || corridor.id}`,
            content: [
                { label: 'ID', value: corridor.id || 'N/A' },
                { label: 'Name', value: corridor.name || 'N/A' },
                { label: 'Service', value: corridor.serviceDetailName || corridor.serviceDetailId || 'N/A' }
            ],
            expanded: false,
            isNested: true
        }));

        content.push({
            label: 'Corridors',
            value: `${microCorridor.corridors.length} corridors`,
            nested: corridorSections
        });
    } else {
        content.push({ label: 'Corridors', value: 'None' });
    }

    return {
        header: 'Micro Corridor',
        content: content,
        expanded: false,
        isNested: false
    };
}

function buildHailSection(hail: any): SupplySection | null {
    if (!hail) return null;

    const content: { label: string; value: string; nested?: SupplySection[] }[] = [
        { label: 'Status', value: hail.status || 'N/A' },
        { label: 'Type', value: hail.type || 'N/A' },
        { label: 'Token', value: hail.token ? hail.token.toString() : 'N/A' },
        { label: 'OTP', value: hail.otp || 'N/A' },
        { label: 'Updated', value: formatDate(hail.updatedAt) || 'N/A' }
    ];

    if (hail.layer) {
        const layerNested: SupplySection = {
            header: 'Layer',
            content: [
                { label: 'Layer ID', value: hail.layer.id || 'N/A' },
                { label: 'Layer Name', value: hail.layer.name || 'N/A' },
                { label: 'Layer Type', value: hail.layer.type || 'N/A' }
            ],
            expanded: false,
            isNested: true
        };
        content.push({
            label: 'Layer',
            value: 'View Details',
            nested: [layerNested]
        });
    }

    if (hail.pickupPoint) {
        const pickupCoordinateNested: SupplySection = {
            header: 'Pickup Coordinate',
            content: [
                { 
                    label: 'Latitude', 
                    value: (hail.pickupPoint.coordinate && hail.pickupPoint.coordinate.lat) 
                        ? hail.pickupPoint.coordinate.lat.toFixed(6) 
                        : 'N/A' 
                },
                { 
                    label: 'Longitude', 
                    value: (hail.pickupPoint.coordinate && hail.pickupPoint.coordinate.lng) 
                        ? hail.pickupPoint.coordinate.lng.toFixed(6) 
                        : 'N/A' 
                }
            ],
            expanded: false,
            isNested: true
        };

        const pickupPointNested: SupplySection = {
            header: 'Pickup Point',
            content: [
                { label: 'Name', value: hail.pickupPoint.name || 'N/A' },
                {
                    label: 'Coordinate',
                    value: 'View Location',
                    nested: [pickupCoordinateNested]
                }
            ],
            expanded: false,
            isNested: true
        };

        content.push({
            label: 'Pickup Point',
            value: 'View Details',
            nested: [pickupPointNested]
        });
    }

    return {
        header: 'Hail',
        content: content,
        expanded: false,
        isNested: false
    };
}

function buildFleetSection(fleet: any): SupplySection | null {
    if (!fleet) return null;
    return {
        header: 'Fleet',
        content: [
            { label: 'Fleet ID', value: fleet.id || 'N/A' },
            { label: 'Fleet Name', value: fleet.name || 'N/A' },
            { label: 'Type', value: fleet.type || 'N/A' },
            { label: 'Enabled', value: fleet.enabled ? 'Yes' : 'No' },
            { label: 'Updated', value: formatDate(fleet.updatedAt) }
        ],
        expanded: false,
        isNested: false
    };
}

function buildSubscriptionSection(subscription: any): SupplySection | null {
    if (!subscription || !subscription.value || subscription.value.length === 0) {
        return null;
    }

    const subscriptionSections: SupplySection[] = subscription.value.map((sub: any, index: number) => ({
        header: `Subscription ${index + 1}: ${sub.serviceDetailName || sub.serviceDetailId}`,
        content: [
            { label: 'Service ID', value: sub.serviceDetailId || 'N/A' },
            { label: 'Service Name', value: sub.serviceDetailName || 'N/A' },
            { label: 'Status', value: sub.status || 'N/A' },
            { label: 'Updated', value: formatDate(sub.updatedAt) }
        ],
        expanded: false,
        isNested: true
    }));

    return {
        header: 'Subscription',
        content: [
            { label: 'Updated', value: formatDate(subscription.updatedAt) },
            {
                label: 'Subscriptions',
                value: `${subscription.value.length} subscriptions`,
                nested: subscriptionSections
            }
        ],
        expanded: false,
        isNested: false
    };
}

function buildDeprioritizationSection(deprioritization: any): SupplySection | null {
    if (!deprioritization || !deprioritization.serviceDetails || deprioritization.serviceDetails.length === 0) {
        return null;
    }

    const serviceNames = deprioritization.serviceDetails
        .map((s: any) => s.name || s.id)
        .join(', ');

    return {
        header: 'Deprioritization',
        content: [
            { label: 'Services', value: serviceNames },
            { label: 'Updated', value: formatDate(deprioritization.updatedAt) }
        ],
        expanded: false,
        isNested: false
    };
}

function buildReserveSection(reserve: any): SupplySection | null {
    if (!reserve) return null;

    const content: { label: string; value: string; nested?: SupplySection[] }[] = [];

    if (reserve.validTill) {
        const validTillDate = new Date(reserve.validTill);
        content.push({ label: 'Valid Till', value: validTillDate.toLocaleString() });
    } else {
        content.push({ label: 'Valid Till', value: 'N/A' });
    }

    content.push({ label: 'Updated', value: formatDate(reserve.updatedAt) });

    if (reserve.meta) {
        const metaNested: SupplySection = {
            header: 'Meta',
            content: [
                { label: 'Context', value: reserve.meta.context || 'N/A' },
                { label: 'Source', value: reserve.meta.source || 'N/A' }
            ],
            expanded: false,
            isNested: true
        };
        content.push({
            label: 'Meta',
            value: 'View Details',
            nested: [metaNested]
        });
    }

    return {
        header: 'Reserve',
        content: content,
        expanded: false,
        isNested: false
    };
}

function buildDropLocationPreferenceSection(dropLocationPreference: any): SupplySection | null {
    if (!dropLocationPreference || !dropLocationPreference.preferences || dropLocationPreference.preferences.length === 0) {
        return null;
    }

    const preferenceSections: SupplySection[] = dropLocationPreference.preferences.map((pref: any, index: number) => ({
        header: `Preference ${index + 1}`,
        content: [
            { label: 'Layer ID', value: pref.layerId || 'N/A' },
            { label: 'Context', value: pref.context || 'N/A' },
            { label: 'Updated', value: formatDate(pref.updatedAt) }
        ],
        expanded: false,
        isNested: true
    }));

    return {
        header: 'Drop Location Preference',
        content: [
            { label: 'Updated', value: formatDate(dropLocationPreference.updatedAt) },
            {
                label: 'Preferences',
                value: `${dropLocationPreference.preferences.length} preferences`,
                nested: preferenceSections
            }
        ],
        expanded: false,
        isNested: false
    };
}

function buildLastMilePreferenceSection(lastMilePreference: any): SupplySection | null {
    if (!lastMilePreference || !lastMilePreference.preferences || lastMilePreference.preferences.length === 0) {
        return null;
    }

    const preferenceSections: SupplySection[] = lastMilePreference.preferences.map((pref: any, index: number) => {
        const distanceRangeNested: SupplySection = {
            header: 'Distance Range',
            content: [
                { 
                    label: 'Min', 
                    value: (pref.distanceRangeInMeters && pref.distanceRangeInMeters.min !== null && pref.distanceRangeInMeters.min !== undefined)
                        ? `${pref.distanceRangeInMeters.min}m`
                        : 'N/A'
                },
                { 
                    label: 'Max', 
                    value: (pref.distanceRangeInMeters && pref.distanceRangeInMeters.max !== null && pref.distanceRangeInMeters.max !== undefined)
                        ? `${pref.distanceRangeInMeters.max}m`
                        : 'N/A'
                }
            ],
            expanded: false,
            isNested: true
        };

        return {
            header: `Preference ${index + 1}`,
            content: [
                { label: 'Mode ID', value: pref.modeId || 'N/A' },
                { label: 'Category ID', value: pref.categoryId || 'N/A' },
                {
                    label: 'Distance Range',
                    value: 'View Range',
                    nested: [distanceRangeNested]
                }
            ],
            expanded: false,
            isNested: true
        };
    });

    return {
        header: 'Last Mile Preference',
        content: [
            { label: 'Updated', value: formatTimestamp(lastMilePreference.updatedAt) || 'N/A' },
            {
                label: 'Preferences',
                value: `${lastMilePreference.preferences.length} preferences`,
                nested: preferenceSections
            }
        ],
        expanded: false,
        isNested: false
    };
}

// Root fields: add new entries here when API adds new top-level fields.
// Use (v) => String for plain values, or formatTimestamp/formatDate for dates.
const ROOT_FIELD_CONFIG: { key: string; label: string; format: (v: any) => string }[] = [
    { key: 'captainId', label: 'Captain ID', format: (v) => v || 'N/A' },
    { key: 'updatedAt', label: 'Updated At', format: formatDate },
    { key: 'deviceId', label: 'Device ID', format: (v) => v || 'N/A' },
    { key: 'lastModifiedOn', label: 'Last Modified On', format: formatDate },
    { key: 'lastTimeChangeEventPublishedAt', label: 'Last Time Change Event Published At', format: formatDate }
];

// Section builders keyed by API field name. Add new section = add one builder + one entry here.
const SECTION_BUILDERS: Record<string, (data: any) => SupplySection | null> = {
    location: buildLocationSection,
    routePreference: buildRoutePreferenceSection,
    microCorridor: buildMicroCorridorSection,
    services: buildServicesSection,
    deprioritization: buildDeprioritizationSection,
    status: buildStatusSection,
    onRideBooking: buildOnRideBookingSection,
    wayPoints: buildWayPointsSection,
    hail: buildHailSection,
    fleet: buildFleetSection,
    subscription: buildSubscriptionSection,
    reserve: buildReserveSection,
    dropLocationPreference: buildDropLocationPreferenceSection,
    lastMilePreference: buildLastMilePreferenceSection
};

export function buildSupplyAttributesData(supplyData: any): SupplyAttributesData {
    if (!supplyData) {
        return { rootFields: [], sections: [] };
    }

    const rootFields = ROOT_FIELD_CONFIG.map(({ key, label, format }) => ({
        label,
        value: format(supplyData[key])
    }));

    const sections: SupplySection[] = [];
    for (const [key, buildSection] of Object.entries(SECTION_BUILDERS)) {
        const section = buildSection(supplyData[key]);
        if (section) sections.push(section);
    }

    return { rootFields, sections };
}
